import { useState, useEffect, useCallback } from 'react';
import { deviceService } from '../services/deviceService';

const mergeDefined = (base = {}, patch = {}) => {
  const next = { ...base };
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      next[key] = value;
    }
  });
  return next;
};

export const useDevices = (school) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all devices
  const fetchDevices = useCallback(async (opts = {}) => {
    const silent = !!opts.silent;
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const data = await deviceService.getAllDevices(school);

      // Merge with existing state so that WS-sourced sensor data isn't wiped
      setDevices(prevDevices => {
        if (prevDevices.length === 0) return data;

        return data.map(newDevice => {
          const existing = prevDevices.find(d => d.id === newDevice.id);
          if (!existing) return newDevice;

          // Keep the fresher sensorData: compare timestamps
          const existingTs = existing.sensorData?.timestamp
            ? new Date(existing.sensorData.timestamp).getTime() : 0;
          const newTs = newDevice.sensorData?.timestamp
            ? new Date(newDevice.sensorData.timestamp).getTime() : 0;

          // If existing (WS) data is newer, preserve it; merge in any new metadata
          if (existingTs >= newTs) {
            return {
              ...newDevice,
              sensorData: existing.sensorData,
              status: existing.status,
            };
          }

          // API data is newer — merge so undefined fields don't overwrite real values
          return {
            ...newDevice,
            sensorData: mergeDefined(existing.sensorData, newDevice.sensorData),
          };
        });
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch devices');
      console.error('Error fetching devices:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [school]);

  // Refresh devices (alias for fetchDevices)
  const refreshDevices = useCallback(() => {
    // Silent refresh: do not flip global loading state to avoid UI flicker
    fetchDevices({ silent: true });
  }, [fetchDevices]);

  // Get device by ID
  const getDevice = useCallback(async (deviceId) => {
    try {
      const device = await deviceService.getDevice(deviceId);
      return device;
    } catch (err) {
      console.error('Error fetching device:', err);
      throw err;
    }
  }, []);

  // Ping device
  const pingDevice = useCallback(async (deviceId) => {
    try {
      const result = await deviceService.pingDevice(deviceId);
      
      // Update the device in the local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.id === deviceId 
            ? { ...device, lastSeen: new Date().toISOString(), status: 'online' }
            : device
        )
      );
      
      return result;
    } catch (err) {
      console.error('Error pinging device:', err);
      throw err;
    }
  }, []);

  // Update device status (for real-time updates)
  const updateDeviceStatus = useCallback((deviceId, updates) => {
    setDevices(prevDevices => 
      prevDevices.map(device => {
        if (device.id !== deviceId) return device;

        // Deep merge sensorData to preserve fields from previous states
        const newSensorData = mergeDefined(device.sensorData, updates.sensorData);

        return {
          ...device,
          ...updates,
          sensorData: newSensorData,
          lastSeen: updates.lastSeen || device.lastSeen,
        };
      })
    );
  }, []);

  // Add new device (for real-time updates)
  const addDevice = useCallback((newDevice) => {
    setDevices(prevDevices => {
      const exists = prevDevices.some(device => device.id === newDevice.id);
      if (exists) {
        return prevDevices.map(device => 
          device.id === newDevice.id ? newDevice : device
        );
      }
      return [...prevDevices, newDevice];
    });
  }, []);

  // Remove device
  const removeDevice = useCallback((deviceId) => {
    setDevices(prevDevices => 
      prevDevices.filter(device => device.id !== deviceId)
    );
  }, []);

  // Delete device
  const deleteDevice = useCallback(async (deviceId) => {
    try {
      await deviceService.deleteDevice(deviceId);
      // Remove from local state
      setDevices(prevDevices => 
        prevDevices.filter(device => device.id !== deviceId)
      );
      return true;
    } catch (err) {
      console.error('Error deleting device:', err);
      throw err;
    }
  }, []);

  // Get devices by status
  const getDevicesByStatus = useCallback((status) => {
    return devices.filter(device => device.status === status);
  }, [devices]);

  // Get devices by type
  const getDevicesByType = useCallback((type) => {
    return devices.filter(device => device.type === type);
  }, [devices]);

  // Get device statistics
  const getDeviceStats = useCallback(() => {
    const stats = {
      total: devices.length,
      online: devices.filter(d => d.status === 'online').length,
      offline: devices.filter(d => d.status === 'offline').length,
      alarm: devices.filter(d => d.status === 'alarm').length,
      detectors: devices.filter(d => d.type === 'detector').length,
      adminConsoles: devices.filter(d => d.type === 'admin').length
    };
    return stats;
  }, [devices]);

  // Initial fetch on mount
  useEffect(() => {
    // Initial fetch should show loading state
    fetchDevices({ silent: false });
  }, [fetchDevices]);

  return {
    devices,
    loading,
    error,
    fetchDevices,
    refreshDevices,
    getDevice,
    pingDevice,
    updateDeviceStatus,
    addDevice,
    removeDevice,
    deleteDevice,
    getDevicesByStatus,
    getDevicesByType,
    getDeviceStats
  };
};
